import { useRef, useEffect } from "react"
import { useTranslation } from "gatsby-plugin-react-i18next"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import styled, { css } from "styled-components"

import InnerWrapper from "components/InnerWrapper"
import Callout from "components/TextElements/Callout"
import colors from "styles/colors"
import text, { transparentText } from "styles/text"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import useMedia from "utils/useMedia"
import media from "styles/media"

export default function Glance() {
  const statRefs = useRef<(HTMLSpanElement | null)[]>([])
  const scrollTriggersRef = useRef<ScrollTrigger[]>([]) // Track only our ScrollTriggers
  const titleWidth = useMedia("883px", "53.193vw", "100%", "100%")
  const card1Offset = useMedia("0", "0", "0", "")
  const card2Offset = useMedia("140px", "8.434vw", "8.984vw", "")
  const card3Offset = useMedia("90px", "5.422vw", "5.762vw", "")
  const card4Offset = useMedia("190px", "11.446vw", "12.207vw", "")
  const card5Offset = useMedia("40px", "2.41vw", "2.539vw", "")
  const innerWidth = useMedia("1015px", "61.145vw", "81.445vw", "89.333vw")
  const startAnimation = useMedia("top 80%", "top 80%", "top 65%", "top 45%")
  const { t } = useTranslation()

  const cardData = [
    {
      stat: "100",
      suffix: "+",
      underline: t("Best-In-Class Platform Partners"),
      color: colors.BlueTurqHorz,
      offset: card1Offset,
    },
    {
      stat: "158",
      suffix: "M",
      underline: t("Accounts"),
      color: colors.BlueTurqHorz,
      offset: card2Offset,
    },
    {
      stat: "2",
      suffix: "+",
      underline: t("Decades of Experience"),
      color: colors.BlueTurqHorz,
      offset: card3Offset,
    },
    {
      stat: "1.7",
      suffix: "k",
      underline: t("Team Members"),
      color: colors.BlueTurqHorz,
      offset: card4Offset,
    },
    {
      stat: "13",
      suffix: "",
      underline: t("Supported Countries"),
      color: colors.BlueTurqHorz,
      offset: card5Offset,
    },
  ]

  const lastCard = () => {
    const index = cardData.length - 1
    return (
      <>
        <NumberLine key={`number-${cardData[index].stat}`} last={true}>
          <FullWrapperLast>
            <StatWrapper>
              <Stat color={cardData[index].color}>
                <span
                  ref={(el) => (statRefs.current[index] = el)}
                  data-value={cardData[index].stat}
                  data-suffix={cardData[index].suffix}
                >
                  0
                </span>
              </Stat>{" "}
            </StatWrapper>
            <LabelWrapper>
              <LabelLast>{cardData[index].underline}</LabelLast>
            </LabelWrapper>
          </FullWrapperLast>
        </NumberLine>
      </>
    )
  }

  const twoCards = (index: number) => {
    return (
      <NumberLine key={`number-${cardData[index].stat}`} last={false}>
        <FullWrapper>
          <StatWrapper>
            <Stat color={cardData[index].color}>
              <span
                ref={(el) => (statRefs.current[index] = el)}
                data-value={cardData[index].stat}
                data-suffix={cardData[index].suffix}
              >
                0
              </span>
            </Stat>{" "}
          </StatWrapper>
          <LabelWrapper>
            <Label>{cardData[index].underline}</Label>
          </LabelWrapper>
        </FullWrapper>
        <FullWrapper>
          <StatWrapper>
            <Stat color={cardData[index + 1].color}>
              <span
                ref={(el) => (statRefs.current[index + 1] = el)}
                data-value={cardData[index + 1].stat}
                data-suffix={cardData[index + 1].suffix}
              >
                0
              </span>
            </Stat>{" "}
          </StatWrapper>
          <LabelWrapper>
            <Label>{cardData[index + 1].underline}</Label>
          </LabelWrapper>
        </FullWrapper>
      </NumberLine>
    )
  }

  // Row-by-row Fade Scale Animation with Custom Settings
  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger)

    // Clear only our previous ScrollTriggers
    scrollTriggersRef.current.forEach((trigger) => trigger.kill())
    scrollTriggersRef.current = []

    // Define rows with custom configurations
    const rows = [
      {
        indices: [0, 1], // Row 1: 100+ and 158M
        configs: [
          { finalValue: 100, suffix: "+", startValue: 80, increment: 1, useCustom: true },
          { finalValue: 158, suffix: "M", startValue: 130, increment: 1, useCustom: true },
        ],
      },
      {
        indices: [2, 3], // Row 2: 2+ and 1.7k
        configs: [
          { finalValue: 2, suffix: "+", startValue: 0, increment: 0, useCustom: false },
          { finalValue: 1.7, suffix: "k", startValue: 0, increment: 0, useCustom: false },
        ],
      },
      {
        indices: [4], // Row 3: 13
        configs: [{ finalValue: 13, suffix: "", startValue: 0, increment: 0, useCustom: false }],
      },
    ]

    // Custom increment animation function
    const createCustomIncrementAnimation = (
      el: HTMLSpanElement,
      finalValue: number,
      suffix: string,
      startValue: number,
      increment: number,
      duration: number,
    ) => {
      // Set initial value
      el.innerText = `${startValue}${suffix}`

      // Calculate steps and timing
      const totalSteps = Math.ceil((finalValue - startValue) / increment)
      const stepDuration = duration / totalSteps

      let currentStep = 0
      let currentValue = startValue

      const stepAnimation = () => {
        if (currentStep < totalSteps && currentValue < finalValue) {
          currentValue = Math.min(currentValue + increment, finalValue)
          el.innerText = `${currentValue}${suffix}`
          currentStep++
          gsap.delayedCall(stepDuration, stepAnimation)
        } else {
          // Ensure exact final value
          el.innerText = `${finalValue}${suffix}`
        }
      }

      // Start immediately (no delay since it's controlled by timeline)
      stepAnimation()
    }

    // Set initial state for all elements
    statRefs.current.forEach((el, index) => {
      if (el) {
        gsap.set(el.parentElement, { opacity: 0, scale: 0.5 })

        // Set initial values based on configuration
        const rowData = rows.find((row) => row.indices.includes(index))
        if (rowData) {
          const configIndex = rowData.indices.indexOf(index)
          const config = rowData.configs[configIndex]
          const startValue = config.startValue
          const hasDecimal = config.finalValue % 1 !== 0
          const formatted = hasDecimal ? startValue.toFixed(1) : Math.floor(startValue).toString()
          el.innerText = `${formatted}${config.suffix}`
        }
      }
    })

    // Create master timeline with ScrollTrigger
    const masterTimeline = gsap.timeline()

    const scrollTrigger = ScrollTrigger.create({
      trigger: statRefs.current[0],
      start: startAnimation,
      toggleActions: "play none none none",
      animation: masterTimeline,
    })

    scrollTriggersRef.current.push(scrollTrigger)

    // Animate each row sequentially
    rows.forEach((row, rowIndex) => {
      const rowTimeline = gsap.timeline()

      row.indices.forEach((elementIndex, positionInRow) => {
        const el = statRefs.current[elementIndex]
        if (!el) return

        const config = row.configs[positionInRow]
        const inRowDelay = positionInRow * 0.1

        // FADE/SCALE ANIMATION STARTS FIRST (at time 0)
        rowTimeline.to(
          el.parentElement,
          {
            opacity: 1,
            scale: 1,
            duration: 0.8,
            ease: "back.out(1.7)",
            delay: inRowDelay,
          },
          0,
        )

        // NUMBER ANIMATION STARTS IN MIDDLE OF FADE-IN (at time 0.4s)
        if (config.useCustom) {
          // Custom increment animation for 100 and 158
          rowTimeline.call(
            () =>
              createCustomIncrementAnimation(
                el,
                config.finalValue,
                config.suffix,
                config.startValue,
                config.increment,
                1.5, // duration
              ),
            [],
            0.4 + inRowDelay, // Start in middle of fade-in
          )
        } else {
          // Smooth animation for others
          const obj = { value: config.startValue }
          const hasDecimal = config.finalValue % 1 !== 0

          rowTimeline.to(
            obj,
            {
              value: config.finalValue,
              duration: 1.2,
              ease: "power2.out",
              delay: inRowDelay,
              onUpdate: () => {
                if (hasDecimal) {
                  const current = obj.value.toFixed(1)
                  el.innerText = `${current}${config.suffix}`
                } else {
                  // Keep original behavior for whole numbers like 13
                  const current = Math.floor(obj.value)
                  el.innerText = `${current}${config.suffix}`
                }
              },
            },
            0.4, // Start in middle of fade-in
          )
        }
      })

      masterTimeline.add(rowTimeline, rowIndex === 0 ? 0 : "=0.1")
    })

    return () => {
      scrollTriggersRef.current.forEach((trigger) => trigger.kill())
      scrollTriggersRef.current = []
    }
  }, [startAnimation])

  return (
    <Wrapper>
      <InnerWrapper setWidth={innerWidth}>
        <Callout
          title={{
            content: t("Galileo at a glance."),
            width: titleWidth,
            color: colors.blue800,
          }}
          kicker={{
            text: t("Our Track Record") ?? "Our Track Record",
            icon: "layers",
            color: colors.blue200,
          }}
          centered
        />
        <CardsWrapper>
          {twoCards(0)}
          {twoCards(2)}
          {lastCard()}
        </CardsWrapper>
      </InnerWrapper>
    </Wrapper>
  )
}

const FullWrapper = styled.div``
const FullWrapperLast = styled.div`
  display: flex;
  justify-content: center;
  align-item: center;
  ${media.fullWidth} {
    width: 600px;
  }
  ${media.desktop} {
    width: 37vw;
  }
  ${media.tablet} {
    width: 55vw;
  }
  ${media.mobile} {
    width: 60vw;
  }
`

const NumberLine = styled.div<{ last: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: ${(props) => (props.last ? "center" : "space-between")};
  align-items: flex-start;
  border-bottom: ${(props) => (props.last ? "none" : `1px solid ${colors.blue50}`)};
  width: 100%;

  ${fresponsive(css`
    padding-top: 25px;
    margin-bottom: 50px;
    min-height: 311px;
  `)}

  ${ftablet(css`
    padding-top: 10px;
    margin-bottom: 40px;
    min-height: 300px;
  `)}
    
  ${fmobile(css`
    width: 335px;
    height: 128px;
    padding-top: 10px;
    margin-bottom: 20px;
    min-height: auto;
  `)}
`

const StatWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

const Stat = styled.p<{ color: string }>`
  position: relative;
  ${text.h4};

  ${transparentText};
  background-image: ${({ color }) => color};
  line-height: 80%;

  ${fresponsive(css`
    font-size: 170px;
  `)}

  ${ftablet(css`
    font-size: 144px;
    line-height: 90%;
  `)}
  ${fmobile(css`
    ${text.h3};
    line-height: 90%;
  `)}
`

const LabelWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  ${media.fullWidth} {
    margin-top: 20px;
    width: 400px;
  }
  ${media.desktop} {
    margin-top: 1.2vw;
    width: 25vw;
  }
  ${media.tablet} {
    margin-top: 1.5vw;
    width: 35vw;
  }
  ${media.mobile} {
    margin-top: 2vw;
    width: 40vw;
  }
`

const LabelLast = styled.p`
  ${text.h5};
  color: ${colors.blue800};
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  text-align: center;
  ${fresponsive(css`
    width: 300px;
    margin-bottom: 30px;
  `)}

  ${ftablet(css`
    width: 300px;
  `)}

  ${fmobile(css`
    ${text.bodyXS};
    width: 120px;
    margin-bottom: 10px;
  `)}
`

const Label = styled.p`
  ${text.h5};
  color: ${colors.blue800};
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  text-align: center;
  ${fresponsive(css`
    width: 250px;
    margin-bottom: 10px;
  `)}

  ${ftablet(css`
    width: 260px;
  `)}

  ${fmobile(css`
    ${text.bodyXS};
    width: 120px;
    margin-bottom: 10px;
  `)}
`

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  will-change: transform;
  transform: translate3d(0, 0, 0, 0);

  height: auto;
  overflow: hidden;

  ${fresponsive(css`
    padding: 150px 0 148px 0;
  `)}

  ${ftablet(css`
    padding: 85px 0 0 0;
  `)}

  ${fmobile(css`
    padding-bottom: 100px;
  `)}
`

const CardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;

  ${fresponsive(css`
    margin-top: 150px;
  `)}

  ${ftablet(css`
    margin-top: 82px;
  `)}

  ${fmobile(css`
    justify-content: center;
    align-items: center;
    margin-top: 40px;
  `)}
`